// SuspensionOptOutCard — Story 12.2 (Fase 4) AC12.
// Toggle "Isentar de suspensão automática" no detalhe do cliente. Cliente VIP
// segue recebendo a régua de avisos, mas nunca é suspenso automaticamente.
// Visível para admin e financeiro (RBAC front; backend valida).

import { useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@control-center/ui';

import { useAuth } from '../../hooks/use-auth';
import { clientsKeys } from '../../hooks/use-clients';
import { useSetClientSuspensionOptOut } from '../../hooks/use-suspension-policy';
import type { ClientEntity } from '../../lib/clients-api';
import { extractErrorMessage } from '../../lib/errors';

export function SuspensionOptOutCard({ client }: { client: ClientEntity }): JSX.Element | null {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.role === 'financeiro';

  const qc = useQueryClient();
  const mutation = useSetClientSuspensionOptOut();
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  if (!canManage) return null;

  const value = optimistic ?? client.skip_auto_suspension;

  async function handleToggle() {
    const next = !value;
    setOptimistic(next);
    try {
      await mutation.mutateAsync({ clientId: client.id, skip: next });
      await qc.invalidateQueries({ queryKey: clientsKeys.detail(client.id) });
      toast.success(
        next
          ? 'Cliente isento de suspensão automática.'
          : 'Cliente voltou a ser suspendível automaticamente.',
      );
    } catch (err) {
      setOptimistic(null); // reverte UI
      toast.error(extractErrorMessage(err, 'Não conseguimos alterar a isenção.'));
    }
  }

  return (
    <div className="rounded-[12px] border border-line bg-panel p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="mt-[2px] flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-panel-3 text-muted"
          >
            <ShieldCheck size={15} strokeWidth={1.8} />
          </span>
          <div>
            <div className="text-[13px] font-semibold text-ink">
              Isentar de suspensão automática
            </div>
            <p
              className="mt-[2px] max-w-md text-[11px] leading-relaxed text-muted-2"
              title="Cliente continuará recebendo avisos de inadimplência, mas nunca será suspenso automaticamente"
            >
              Cliente continuará recebendo avisos de inadimplência, mas nunca será suspenso
              automaticamente (VIP).
            </p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={value}
          aria-label="Isentar de suspensão automática"
          disabled={mutation.isPending}
          onClick={() => void handleToggle()}
          className={cn(
            'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
            value ? 'bg-ink' : 'bg-line-2',
          )}
        >
          <span
            className={cn(
              'inline-block h-5 w-5 transform rounded-full bg-panel transition-transform',
              value ? 'translate-x-[22px]' : 'translate-x-[2px]',
            )}
          />
        </button>
      </div>
    </div>
  );
}
